/**
 * RoundView Component - PadelPulse Stitch Design
 * Displays matches for a specific round with compact card layout and result reporting
 */

import { apiService } from "../services/api";
import type { Match, League, Round, LeagueStanding } from "../types/index";
import { applyBaseLayout, setupHeaderListeners } from "../layouts/LayoutManager";
import { renderMatchCard } from "../components/MatchCard";
import { renderLoadingState } from "../utils/ui";
import { ReportResultComponent } from "../components/ReportResultComponent";
import { ConfirmationModal } from "../components/ConfirmationModal";

export class RoundView {
  private container: HTMLElement;
  private leagueId: number;
  private roundNumber: number;
  private league: League | null = null;
  private round: Round | null = null;
  private standings: LeagueStanding[] = [];
  private confirmationModal: ConfirmationModal;

  constructor(container: HTMLElement, leagueId: number, roundNumber: number) {
    this.container = container;
    this.leagueId = leagueId;
    this.roundNumber = roundNumber;
    this.confirmationModal = new ConfirmationModal();
  }

  async render(): Promise<void> {
    this.container.innerHTML = this.getLoadingHTML();
    try {
      const [league, roundData, standingsData] = await Promise.all([
        apiService.getLeagueById(this.leagueId),
        apiService.getRound(this.leagueId, this.roundNumber),
        apiService.getStandings(this.leagueId)
      ]);

      this.league = league;
      this.round = roundData.data || roundData;
      this.standings = Array.isArray(standingsData)
        ? standingsData
        : standingsData.data || [];

      if (!this.league || !this.round) {
        throw new Error("Failed to load league or round data.");
      }

      const contentHTML = this.getRoundHTML();
      const layoutHTML = applyBaseLayout({
        activePage: "leagues",
        container: this.container,
        content: contentHTML,
        title: `Round ${this.round.roundNumber} - ${this.league.name}`,
        description: `Matches for round ${this.round.roundNumber} of the ${this.league.name} league.`
      });

      this.container.innerHTML = layoutHTML;
      this.setupEventListeners();
    } catch (error) {
      console.error("Failed to load round:", error);
      this.container.innerHTML = `
        <div class="flex items-center justify-center min-h-screen">
          <div class="text-center">
            <span class="material-symbols-outlined text-6xl text-error mb-4">error</span>
            <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">Failed to load round data</p>
            <button class="btn-primary mt-4" onclick="window.location.hash = '#home'">Back to Home</button>
          </div>
        </div>
      `;
    }
  }

  private getLoadingHTML(): string {
    return renderLoadingState("Loading round…");
  }

  private getRoundHTML(): string {
    if (!this.league || !this.round) return "";

    const pendingMatches = this.round.matches.filter(m => m.status === "pending").length;
    const reportedMatches = this.round.matches.length - pendingMatches;

    return `
      <!-- Breadcrumb & Header -->
      <div class="mb-8">
        <nav class="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-4 uppercase tracking-widest">
          <a href="#league/${this.leagueId}" class="hover:text-primary daylight:hover:text-day-ink transition-colors">${this.league.name}</a>
          <span class="material-symbols-outlined text-sm">chevron_right</span>
          <span class="text-primary daylight:text-day-ink">Round ${this.round.roundNumber}</span>
        </nav>

        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white">Round ${this.round.roundNumber} Matches</h1>
          <div class="flex items-center gap-3">
            <span class="px-3 py-1 bg-warning/10 text-warning rounded-md text-xs font-bold border border-warning/20">
              ${pendingMatches} Pending
            </span>
            <div class="h-4 w-px bg-slate-200 dark:bg-slate-700 daylight:bg-concrete-600"></div>
            <span class="px-3 py-1 bg-accent/10 text-accent rounded-md text-xs font-bold border border-accent/20">
              ${reportedMatches} Reported
            </span>
          </div>
        </div>
      </div>

      <!-- Matches List -->
      <div class="space-y-3 mb-12" id="matches-container">
        ${this.round.matches
          .map((match, index) => this.createMatchCardHTML(match, index + 1))
          .join("")}
      </div>

      <!-- Load More Button -->
      <div class="flex flex-col items-center">
        <button id="load-more-btn" class="group flex items-center gap-3 px-6 py-3 bg-white dark:bg-slate-800 daylight:bg-day-card border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 hover:bg-slate-50 dark:hover:bg-slate-700 daylight:hover:bg-day-sunken rounded-xl transition-all font-bold text-sm">
          <span class="text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">Load more matches</span>
          <span class="material-symbols-outlined text-slate-400 group-hover:translate-y-1 transition-transform">expand_more</span>
        </button>
        <p class="mt-4 text-xs text-slate-500 daylight:text-day-ink-muted font-medium">Showing all ${this.round.matches.length} matches</p>
      </div>

      <!-- Progress Widget (Sticky on Desktop) -->
      <div class="fixed bottom-6 right-6 hidden lg:block">
        <div class="bg-white dark:bg-card-dark daylight:bg-day-card border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-4 rounded-2xl shadow-2xl daylight:shadow-day-lg w-64">
          <h4 class="font-black text-sm mb-3 text-slate-900 dark:text-white daylight:text-day-ink">Round Progress</h4>
          <div class="w-full bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken h-2 rounded-full mb-4 overflow-hidden">
            <div class="bg-primary h-full rounded-full transition-all" style="width: ${
              this.round.matches.length > 0
                ? ((reportedMatches / this.round.matches.length) * 100).toFixed(0)
                : 0
            }%"></div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <span class="text-[10px] uppercase font-bold text-slate-400 block">Finished</span>
              <span class="text-2xl font-black text-accent">${reportedMatches}</span>
            </div>
            <div>
              <span class="text-[10px] uppercase font-bold text-slate-400 block">Remaining</span>
              <span class="text-2xl font-black text-warning">${pendingMatches}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private createMatchCardHTML(match: Match, courtNumber: number): string {
    return renderMatchCard({
      match,
      courtNumber,
      leagueId: this.leagueId,
      showRankings: this.getRankingMap()
    });
  }

  private getRankingMap(): Map<number, number> {
    const rankMap = new Map<number, number>();
    this.standings.forEach((standing, index) => {
      if (standing.playerId) {
        rankMap.set(standing.playerId, index + 1);
      }
    });
    return rankMap;
  }

  private setupEventListeners(): void {
    // Setup header listeners
    setupHeaderListeners(this.container, {
      onLogout: () => {
        localStorage.removeItem("authToken");
        window.location.hash = "#login";
      }
    });

    // Report/Edit match buttons
    this.container.querySelectorAll(".report-match-btn").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const matchId = parseInt(
          (e.currentTarget as HTMLElement).getAttribute("data-match-id") || "0"
        );
        const match = this.round?.matches.find((m) => m.id === matchId);
        if (match) {
          this.showReportModal(match);
        }
      });
    });

    // Load more button
    const loadMoreBtn = this.container.querySelector("#load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => {
        // Could implement pagination here if needed
        alert("All matches are already loaded");
      });
    }
  }

  private showReportModal(match: Match) {
    // 1. Create Overlay
    const overlay = document.createElement("div");
    overlay.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200";
    overlay.id = "report-modal-overlay";

    // 2. Create Modal Container (Stitch Card Styling)
    const modalContainer = document.createElement("div");
    modalContainer.className = "relative w-full max-w-2xl bg-white dark:bg-surface-dark daylight:bg-day-raised rounded-2xl shadow-2xl daylight:shadow-day-modal border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200";
    
    overlay.appendChild(modalContainer);
    document.body.appendChild(overlay);

    const reportComponent = new ReportResultComponent(
      modalContainer,
      match,
      async (data) => {
        try {
          await apiService.updateMatch(match.id, {
            sets: data.sets,
            status: "reported"
          } as any);
          this.closeModal(overlay);
          this.render(); // Re-render to update UI
        } catch (error) {
          console.error("Failed to update match:", error);
          alert("Failed to save match result");
        }
      }
    );

    reportComponent.render();

    // 3. Event Listeners
    const closeWithCheck = async () => {
      if (reportComponent.isDirty) {
        const confirmed = await this.confirmationModal.open(
          "Unsaved Changes",
          "You have unsaved changes. Are you sure you want to discard them?",
          "Discard Changes",
          true
        );
        if (!confirmed) return;
      }
      this.closeModal(overlay);
    };

    // Listen for custom close event from component (Cancel button)
    modalContainer.addEventListener('close-report-modal', () => closeWithCheck());
  }

  private closeModal(overlay: HTMLElement) {
    overlay.classList.add("fade-out", "animate-out");
    setTimeout(() => overlay.remove(), 200);
  }
}